import classNames from 'classnames';
import { observer } from 'mobx-react';
import React, { useState, useRef, useEffect } from 'react';
import { useI18n } from '@udemy/i18n';
import { Button } from '@udemy/react-core-components';
import { debounce, safelySetInnerHTML } from '@udemy/shared-utils';
import styles from "./smart-bar-fixed-height.module.css";
import { SmartBarTimer } from './smart-bar-timer.react-component';

/**
 * Props for the BasicBarContentWithTruncation component
 */

/**
 * No character-counting constants needed with CSS line-clamp!
 * The browser's layout engine handles all text measurement automatically.
 * This makes the solution script-agnostic and typography-independent.
 */

/**
 * BasicBarContentWithTruncation displays smart bar content with CSS line-clamp truncation.
 *
 * Key Features:
 * - Pure CSS line-clamp solution (script-agnostic, typography-independent)
 * - ResizeObserver-based truncation detection (no character counting!)
 * - Smart desktop behavior: clamps to 1 line only if content exceeds 2 lines
 * - Mobile: always clamps to 2 lines
 * - Expand/collapse functionality with "Show more/less" button
 * - Automatic collapse when content fits naturally
 *
 * Truncation Strategy (CSS-based):
 * 1. Use ResizeObserver to detect when content is actually truncated
 * 2. Compare scrollHeight vs clientHeight to determine truncation
 * 3. Calculate natural line count using lineHeight
 * 4. Apply appropriate CSS line-clamp class based on device and line count
 * 5. Show "Show more" button only when truncation is active or content is expanded
 */
const BasicBarContentWithTruncationComponent = ({
  className,
  data,
  onLinkClick,
  onActionURLClick,
  hasCta = false,
  isMobile = false,
  onExpandToggle
}) => {
  var _data$daysToShowTimer;
  const {
    gettext
  } = useI18n();
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef(null);
  const [needsTruncation, setNeedsTruncation] = useState(false);

  /**
   * Check if content needs truncation.
   * Similar to v2: Temporarily removes clamping to measure natural height.
   * Mobile: Check if exceeds 2 lines
   * Desktop: Check if exceeds 2 lines (if yes, we'll clamp to 1 line)
   *
   * Uses debouncing to batch rapid resize events and prevent ResizeObserver loop errors.
   */
  useEffect(() => {
    if (!contentRef.current) return;
    let lastTruncationState = false;
    const checkTruncation = debounce(() => {
      if (!contentRef.current) return;

      // Temporarily remove any clamping to measure natural height
      const currentClass = contentRef.current.className;
      contentRef.current.className = styles['content-text'];

      // Get line height to calculate how many lines the content would take
      const computedStyle = window.getComputedStyle(contentRef.current);
      const lineHeight = parseFloat(computedStyle.lineHeight);
      const scrollHeight = contentRef.current.scrollHeight;

      // Restore original class immediately to minimize layout thrashing
      contentRef.current.className = currentClass;

      // Calculate number of lines
      const lines = Math.round(scrollHeight / lineHeight);

      // For both mobile and desktop: truncate if content exceeds 2 lines
      const shouldTruncate = lines > 2;

      // Only update state if truncation state actually changed
      // This prevents unnecessary re-renders and further resize observations
      if (shouldTruncate !== lastTruncationState) {
        lastTruncationState = shouldTruncate;
        setNeedsTruncation(shouldTruncate);
      }
    }, 10);

    // Initial check
    checkTruncation();
    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(contentRef.current);
    return () => {
      checkTruncation.cancel();
      resizeObserver.disconnect();
    };
  }, [data.title, data.subtitle]);

  /**
   * Show button if truncated OR if expanded (so we can collapse)
   */
  const showButton = needsTruncation || isExpanded;

  /**
   * Auto-collapse when content no longer needs truncation
   * This handles window resizing - if window widens and content fits naturally,
   * we collapse back to clean state
   */
  useEffect(() => {
    if (!needsTruncation && isExpanded) {
      setIsExpanded(false);
    }
  }, [needsTruncation, isExpanded]);

  /**
   * Determine CSS class for content based on state.
   * Matches v2 behavior exactly.
   */
  const getContentClass = () => {
    if (isExpanded) {
      return classNames(styles['content-text'], styles.expanded);
    }
    if (!needsTruncation) {
      return styles['content-text'];
    }

    // Desktop: clamp at 1 line, Mobile: clamp at 2 lines
    return classNames(styles['content-text'], isMobile ? styles['clamped-mobile'] : styles['clamped-desktop']);
  };

  /**
   * Handle toggle button click
   */
  const handleToggle = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    onExpandToggle === null || onExpandToggle === void 0 || onExpandToggle(newState);
  };

  // Check if timer is enabled and will be displayed
  const hasTimer = !!(data.enableTimer && data.endTime);

  // Determine alignment based on CTA presence
  const contentAlignmentClass = hasCta ? styles['basic-bar-content--left-aligned'] : styles['basic-bar-content--center-aligned'];

  /**
   * Render headline content (title + subtitle).
   * Matches v2's SmartBarHeadline structure.
   */
  const renderHeadline = () => {
    const handleLinkClick = e => {
      const target = e.target;
      const anchor = target.closest('a');
      if (anchor !== null && anchor !== void 0 && anchor.href) {
        onLinkClick(anchor.href, 'copy_url');
      }
    };
    const copy = /*#__PURE__*/React.createElement("span", {
      onClick: handleLinkClick,
      role: "presentation",
      "data-testid": "smart-bar-copy"
    }, /*#__PURE__*/React.createElement("span", Object.assign({
      className: classNames('ud-text-bold ud-text-with-links', styles['smart-bar__title'])
    }, safelySetInnerHTML({
      descriptionOfCaller: 'basic-with-truncation:smart-bar-title',
      html: data.title || '',
      dataPurpose: 'smart-bar-title',
      domPurifyConfig: {
        ADD_ATTR: ['target']
      }
    }))), data.subtitle && /*#__PURE__*/React.createElement(React.Fragment, null, ' ', /*#__PURE__*/React.createElement("span", Object.assign({
      className: "ud-text-with-links",
      role: "presentation"
    }, safelySetInnerHTML({
      descriptionOfCaller: 'basic-with-truncation:smart-bar-subtitle',
      html: data.subtitle,
      dataPurpose: 'smart-bar-subtitle',
      domPurifyConfig: {
        ADD_ATTR: ['target']
      }
    })))));
    if (data.action_url) {
      return /*#__PURE__*/React.createElement("a", {
        onClick: onActionURLClick,
        "data-testid": "smart-bar-action-url",
        href: data.action_url
      }, copy);
    }
    return copy;
  };
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames('ud-text-sm', styles['basic-bar-content--inline-timer'], contentAlignmentClass),
    "data-testid": "basic-with-truncation"
  }, /*#__PURE__*/React.createElement("div", {
    ref: contentRef,
    className: getContentClass()
  }, hasTimer && data.endTime && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SmartBarTimer, {
    endTime: typeof data.endTime === 'string' ? data.endTime : data.endTime.toISOString(),
    shouldShowSeconds: !!data.shouldShowSeconds,
    daysToShowTimer: (_data$daysToShowTimer = data.daysToShowTimer) !== null && _data$daysToShowTimer !== void 0 ? _data$daysToShowTimer : 0,
    isInline: true
  }), ' '), renderHeadline()), showButton && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Button, {
    className: styles['smart-bar__truncation-toggle'],
    udStyle: "link",
    onClick: handleToggle
  }, isExpanded ? gettext('Show less') : gettext('Show more')))));
};
BasicBarContentWithTruncationComponent.displayName = 'BasicBarContentWithTruncation';
export const BasicBarContentWithTruncation = observer(BasicBarContentWithTruncationComponent);
//# sourceMappingURL=basic-with-truncation.react-component.js.map